import Input from "components/input"
import { SwitchButton } from "components/switchButton"
import { Permissions } from "../permissions"
import { HttpError, useUpdate } from "@refinedev/core";
import { IOrgRoleForm, IPermission, IOrgRoleDetail } from "interfaces";
import Button from "components/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import toast from "react-hot-toast";
import { Label } from "components/label";
import { FullScreenLoading } from "components/fullScreenLoading";

interface RoleFormProps {
  id: string;
  orgRole?: IOrgRoleDetail;
}

const EditOrgRoleSchema = z.object({
  is_active: z.boolean(),
  organization_id: z.string().min(1, "This field is required"),
  name: z.string().min(1, "This field is required").max(50, "Text length is too long"),
  description: z.string().max(100, "Text length is too long"),
  permissions: z.array(z.object({
    resource: z.string(),
    app: z.string(),
    actions: z.array(z.string()),
    name: z.string()
  }))
}).superRefine(({ permissions }, ctx) => {
  if (permissions.every(item => item.actions.length === 0)) {
    ctx.addIssue({
      path: ['permissions'],
      code: "custom",
      message: "Action is required"
    });
  }
});

export const EditRoleForm = ({ orgRole, id }: RoleFormProps) => {

  const navigate = useNavigate();
  const { register, resetField, formState: { errors }, control, watch, handleSubmit } = 
    useForm<IOrgRoleForm, HttpError, IOrgRoleForm>({
      defaultValues: {
        name: orgRole?.role.name,
        description: orgRole?.role.description,
        is_active: orgRole?.role.is_active,
        organization_id: orgRole?.role.organization_id,
        permissions: orgRole?.role_permission,
      },
      resolver: zodResolver(EditOrgRoleSchema)
    });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: IOrgRoleForm) => {
    const submitValues = {...values};
    mutate({
      resource: "organization/role",
      id,
      invalidates: [],
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        ...submitValues,
        id
      },
    },
    {
      onSuccess: () => {
        toast.success("Update successfully");
        navigate(`/organization/roles/${id}`);
      },
    });
  }

  return (
    <form className="w-full flex flex-col gap-y-3" onSubmit={handleSubmit(onSubmit)}>
      <div className="divide-y divide-gray-100 box-container p-0">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-2">
              <Label 
                required
                id="status" 
                name="Status"  
              />
              <Controller
                control={control}
                name="is_active"
                defaultValue={true}
                render={({ field: { onChange, value = true, ref } }) => (
                  <SwitchButton checked={value} onChange={onChange} />
                )}
              />
            </div>
            <div className="sm:col-span-4">
              <Label 
                required
                id="organization_id" 
                name="Organization name"  
              />
              <Input 
                id="name"
                type="text"
                placeholder="Organization name"
                value={orgRole?.role.organization_name}
                disabled={true}
              />
            </div>
            <div className="sm:col-span-2">
              <Label 
                required
                id="name" 
                name="Role name"  
              />
              <Input 
                id="name"
                type="text"
                placeholder="Role name"
                maxLength={50}
                error={errors["name"]}
                {...register("name")}
              />
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("name") ?? "").length}/50</p>
                  {errors["name"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["name"].message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
            <div className="sm:col-span-4">
              <Label 
                id="description" 
                name="Role description"  
              />
              <Input 
                id="description"
                type="text"
                placeholder="Role description"
                maxLength={100}
                error={errors["description"]}
                {...register("description")}
              />
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("description") ?? "").length}/100</p>
                {errors["description"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["description"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Controller
        control={control}
        name="permissions"
        render={({ field: { onChange, value, ref } }) => (watch("organization_id") ?? "") ? (
          <div className="box-container p-0">
            <div className="px-4 py-6 sm:px-6">
              <Label 
                required
                id="permissions" 
                name="Access Permission"
              />
              <Permissions 
                organizationId={(watch("organization_id") ?? "")}
                data={value}
                onChange={(data: IPermission[]) => onChange(data)}
                isOwner={orgRole?.role.name === "Owner"}
                resetField={resetField}
              />
              {errors["permissions"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["permissions"]?.message}
                  </span>
                </p> 
              ): null}
            </div>
          </div>
        ) : <></>}
      />
      <div className="w-full px-4 py-4 sm:px-6 bg-white">
        <div className="flex items-center justify-end gap-x-4">
          <Link to={`/organization/roles/${id}`}>
            <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
              CANCEL
            </Button>
          </Link>
          <Button className="w-20" type="submit">
            SAVE
          </Button>
        </div>
      </div>
      {isLoading ? <FullScreenLoading /> : <></>}
    </form>
  )
} 